// src/components/ParticipantsLayout.jsx
import React, { useState } from "react";
import LocalPreview from "./LocalPreview";
import RemoteTile from "./RemoteTile";

/**
 * ParticipantsLayout
 *
 * Regelt de layout van de cameratiles:
 *
 * - mode === "right": verticale kolom rechts van de video
 *   - showSelfView = true:
 *       LocalPreview boven
 *       daaronder alle remotes
 *       BIJ 0 REMOTES: Local boven + zwarte tile "Wachten op deelnemer…" eronder
 *   - showSelfView = false:
 *       remoteCount = aantal remotes (excl. jij)
 *       - remoteCount === 0:
 *           1 placeholder-tile ("Wachten op deelnemer…")
 *       - remoteCount === 1:
 *           1 placeholder-tile (Bond-logo) + 1 remote
 *       - remoteCount >= 2:
 *           alleen remotes (geen eigen tile meer)
 *
 * - mode === "bottom": strip onder de video
 *   - showSelfView = true:
 *       LocalPreview links, daarachter alle remotes
 *   - showSelfView = false:
 *       remoteCount === 0:
 *         1 placeholder-tile
 *       remoteCount === 1:
 *         2 tiles: placeholder (Bond) + remote → zelfde totale breedte als bij 2 tiles
 *       remoteCount >= 2:
 *         alleen remotes
 *
 * Props:
 *  - mode: "right" | "bottom"
 *  - layout: { rightColPct, bottomVh, tileAspect, ... } (uit App.js)
 *  - peerStreams: Map(peerId -> MediaStream)
 *  - soundEnabled: boolean
 *  - showTiles: boolean (bij video-only fullscreen -> false)
 *  - showSelfView: boolean (true = jezelf zien, false = eigen tile verbergen)
 *  - onLocalStreamChange(stream | null)
 *  - onToggleChat?: () => void   // chat open/dicht vanuit jouw eigen tile
 */
export default function ParticipantsLayout({
  mode,
  layout,
  peerStreams,
  soundEnabled,
  showTiles = true,
  showSelfView = true,
  onLocalStreamChange,
  onToggleChat,
  expectedRemoteCount,   // 👈 NIEUW
  onLocalMicChange,      // 👈 NIEUW
}) {

  // 🔊 Lokale audio-voorkeuren per peer (alleen voor deze gebruiker)
  const [audioPrefs, setAudioPrefs] = useState({});

  if (!showTiles) return null;

  const getVolume = (peerId) =>
    audioPrefs[peerId]?.volume !== undefined ? audioPrefs[peerId].volume : 1;

  const getMuted = (peerId) =>
    audioPrefs[peerId]?.muted !== undefined ? audioPrefs[peerId].muted : false;

  function handleVolumeChange(peerId, volume) {
    setAudioPrefs((prev) => ({
      ...prev,
      [peerId]: {
        volume,
        // slider helemaal naar 0 = muted, >0 = unmute
        muted: volume === 0 ? true : false,
      },
    }));
  }

  function handleToggleMute(peerId) {
    setAudioPrefs((prev) => {
      const cur = prev[peerId] || { volume: 1, muted: false };
      return {
        ...prev,
        [peerId]: {
          volume: cur.volume ?? 1,
          muted: !cur.muted,
        },
      };
    });
  }

   const items = Array.from(peerStreams.entries()).map(([peerId, stream]) => ({
    peerId,
    stream,
  }));
  const remoteCount = items.length;

  // Verwacht aantal remotes op basis van session:state (peerCount - 1)
  const expectedRemotes =
    typeof expectedRemoteCount === "number"
      ? Math.max(expectedRemoteCount, remoteCount)
      : remoteCount;

  // Hoeveel tiles missen er t.o.v. wat we verwachten?
  const missingRemotes = Math.max(0, expectedRemotes - remoteCount);


  const tileAspectRight = layout.tileAspect || "4 / 5";
  const tileAspectBottom = layout.tileAspect || "4 / 3";

  function BondPlaceholder({ message, align = "center" }) {
    return (
      <div
        style={{
          position: "relative",
          background: "#020617",
          borderRight: "1px solid #020617",
          borderBottom: "1px solid #020617",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: align === "left" ? "flex-start" : "center",
            justifyContent: "center",
            padding: 12,
            color: "#e5e7eb",
          }}
        >
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              opacity: 0.9,
            }}
          >
            BOND
          </div>
          {message && (
            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: "#9ca3af",
                textAlign: align === "left" ? "left" : "center",
              }}
            >
              {message}
            </div>
          )}
        </div>
      </div>
    );
  }

  function WaitingPlaceholder() {
    return (
           <div
        style={{
          position: "relative",
          background: "#000",
          borderRight: "1px solid #020617",
          borderBottom: "1px solid #020617",
        }}
      >

        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#bbb",
            fontSize: 14,
            padding: 16,
            textAlign: "center",
          }}
        >
          Wachten op deelnemer…
        </div>
      </div>
    );
  }

    function EmptyPeerSlot() {
    return (
      <div
        style={{
          position: "relative",
          background: "#020617",
          borderRight: "1px solid #020617",
          borderBottom: "1px solid #020617",
        }}
      />
    );
  }


  // ===== RIGHT COLUMN (typisch bij ≤4 tiles) =====
  if (mode === "right") {
    // SELF-VIEW AAN → bestaand gedrag
    if (showSelfView) {
      const hasRemotes = remoteCount > 0;
      const rowCount = hasRemotes ? 1 + remoteCount : 2;

           const colStyle = {
        flex: `0 0 ${layout.rightColPct}%`,
        display: "grid",
        gridTemplateRows: `repeat(${rowCount}, 1fr)`,
        gridTemplateColumns: "1fr",
        gap: 0,
        padding: 0,
        minHeight: 0,
            height: "100%", 
        background: "#000",
      };


      return (
        <div style={colStyle}>
          {/* Local tile boven: nu mét optionele chat-toggle */}
                   <LocalPreview
            tileAspect={tileAspectRight}
            defaultMirror={true}
            onStreamChange={onLocalStreamChange}
            onToggleChat={onToggleChat}
            onMicChange={onLocalMicChange}
          />

          {hasRemotes ? (
            items.map(({ peerId, stream }) => (
              <RemoteTile
                key={peerId}
                peerId={peerId}
                stream={stream}
                soundEnabled={soundEnabled}
                tileAspect={tileAspectRight}
                volume={getVolume(peerId)}
                muted={getMuted(peerId)}
                onVolumeChange={handleVolumeChange}
                onToggleMute={handleToggleMute}
              />
            ))
          ) : (
            <WaitingPlaceholder />
          )}
        </div>
      );
    }

       // SELF-VIEW UIT
    // remoteCount === 0 → alleen wacht-placeholder
    if (remoteCount === 0) {
      const colStyle = {
        flex: `0 0 ${layout.rightColPct}%`,
        display: "grid",
        gridTemplateRows: "1fr",
        gridTemplateColumns: "1fr",
        gap: 0,
        padding: 0,
        minHeight: 0,
            height: "100%", 
        background: "#000",
      };

      return (
        <div style={colStyle}>
          <WaitingPlaceholder />
        </div>
      );
    }


        // remoteCount === 1 → 2 rijen:
    //  - boven: Bond-logo placeholder (jijzelf)
    //  - onder: remote
    if (remoteCount === 1) {
      const colStyle = {
        flex: `0 0 ${layout.rightColPct}%`,
        display: "grid",
        gridTemplateRows: "repeat(2, 1fr)",
        gridTemplateColumns: "1fr",
        gap: 0,
        padding: 0,
        minHeight: 0,
            height: "100%", 
        background: "#000",
      };

      const onlyRemote = items[0];
      return (
        <div style={colStyle}>
          <BondPlaceholder message="Je eigen cam is verborgen." />
          <RemoteTile
            key={onlyRemote.peerId}
            peerId={onlyRemote.peerId}
            stream={onlyRemote.stream}
            soundEnabled={soundEnabled}
            tileAspect={tileAspectRight}
            volume={getVolume(onlyRemote.peerId)}
            muted={getMuted(onlyRemote.peerId)}
            onVolumeChange={handleVolumeChange}
            onToggleMute={handleToggleMute}
          />
        </div>
      );
    }


    // remoteCount >= 2 → alléén remotes, geen eigen tile meer
    {
      const rowCount = remoteCount;
            const colStyle = {
        flex: `0 0 ${layout.rightColPct}%`,
        display: "grid",
        gridTemplateRows: `repeat(${rowCount}, 1fr)`,
        gridTemplateColumns: "1fr",
        gap: 0,
        padding: 0,
        minHeight: 0,
            height: "100%", 
        background: "#000",
      };

      return (
        <div style={colStyle}>
          {items.map(({ peerId, stream }) => (
            <RemoteTile
              key={peerId}
              peerId={peerId}
              stream={stream}
              soundEnabled={soundEnabled}
              tileAspect={tileAspectRight}
              volume={getVolume(peerId)}
              muted={getMuted(peerId)}
              onVolumeChange={handleVolumeChange}
              onToggleMute={handleToggleMute}
            />
          ))}
        </div>
      );
    }
  }

  // ===== BOTTOM STRIP (typisch bij ≥5 tiles of smal scherm) =====
  if (mode === "bottom") {
        // SELF-VIEW AAN → strip onder video
    if (showSelfView) {
      const totalTiles = 1 + expectedRemotes; // 1 local + expected remotes
            const stripStyle = {
        height: `${layout.bottomVh}vh`,
        display: "grid",
        gridTemplateColumns: `repeat(${totalTiles}, 1fr)`,
        gridTemplateRows: "1fr",
        gap: 0,
        padding: 0,
        overflow: "hidden",
        background: "#000",
      };


      return (
        <div style={stripStyle}>
          {/* Local tile links, mét chat-toggle én mic-change */}
          <LocalPreview
            tileAspect={tileAspectBottom}
            defaultMirror={true}
            onStreamChange={onLocalStreamChange}
            onToggleChat={onToggleChat}
            onMicChange={onLocalMicChange}
          />

          {/* Remotes naast elkaar in de strip */}
          {items.map(({ peerId, stream }) => (
            <RemoteTile
              key={peerId}
              peerId={peerId}
              stream={stream}
              soundEnabled={soundEnabled}
              tileAspect={tileAspectBottom}
              volume={getVolume(peerId)}
              muted={getMuted(peerId)}
              onVolumeChange={handleVolumeChange}
              onToggleMute={handleToggleMute}
            />
          ))}

          {/* Lege slots voor remotes waar nog geen stream van is */}
          {Array.from({ length: missingRemotes }).map((_, i) => (
            <EmptyPeerSlot key={`missing-b-${i}`} />
          ))}
        </div>
      );
    }


    // SELF-VIEW UIT

      // remoteCount === 0 → één placeholder
    if (remoteCount === 0) {
      const stripStyle = {
        height: `${layout.bottomVh}vh`,
        display: "grid",
        gridTemplateColumns: "1fr",
        gridTemplateRows: "1fr",
        gap: 0,
        padding: 0,
        overflow: "hidden",
        background: "#000",
      };

      return (
        <div style={stripStyle}>
          <WaitingPlaceholder />
        </div>
      );
    }


    // remoteCount === 1 → 2 tiles breed:
    //   [ Bond-placeholder | remote ]
    if (remoteCount === 1) {
           const stripStyle = {
        height: `${layout.bottomVh}vh`,
        display: "grid",
        gridTemplateColumns: "1fr",
        gridTemplateRows: "1fr",
        gap: 0,
        padding: 0,
        overflow: "hidden",
        background: "#000",
      };

      const onlyRemote = items[0];
      return (
        <div style={stripStyle}>
          <BondPlaceholder
            message="Je eigen cam is verborgen."
            align="left"
          />
          <RemoteTile
            key={onlyRemote.peerId}
            peerId={onlyRemote.peerId}
            stream={onlyRemote.stream}
            soundEnabled={soundEnabled}
            tileAspect={tileAspectBottom}
            volume={getVolume(onlyRemote.peerId)}
            muted={getMuted(onlyRemote.peerId)}
            onVolumeChange={handleVolumeChange}
            onToggleMute={handleToggleMute}
          />
        </div>
      );
    }

        // remoteCount >= 2 → alleen remotes, grid past zich aan aan aantal
    {
      const stripStyle = {
        height: `${layout.bottomVh}vh`,
        display: "grid",
        gridTemplateColumns: `repeat(${remoteCount}, 1fr)`,
        gridTemplateRows: "1fr",
        gap: 0,
        padding: 0,
        overflow: "hidden",
        background: "#000",
      };

      return (
        <div style={stripStyle}>
          {items.map(({ peerId, stream }) => (
            <RemoteTile
              key={peerId}
              peerId={peerId}
              stream={stream}
              soundEnabled={soundEnabled}
              tileAspect={tileAspectBottom}
              volume={getVolume(peerId)}
              muted={getMuted(peerId)}
              onVolumeChange={handleVolumeChange}
              onToggleMute={handleToggleMute}
            />
          ))}
        </div>
      );
    }

  }

  return null;
}
